import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import api from '../../lib/api';
import { toast } from 'sonner';
import { clearSettingsCache } from '../../lib/settings';
import { 
  Building2, 
  Save, 
  Upload,
  Hash,
  MessageCircle,
  FileText,
  LayoutDashboard,
  Settings as SettingsIcon,
  Eye,
  RefreshCw,
  AlertTriangle,
  Trash2
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../components/ui/alert-dialog';
import { useAuth } from '../../contexts/AuthContext';

const Settings = () => {
  const { logout } = useAuth();
  const [activeTab, setActiveTab] = useState('company');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  
  // Settings state
  const [companySettings, setCompanySettings] = useState({
    company_name: 'Jai Matadi Grey Order Management',
    address: '',
    phone: '',
    email: '',
    logo_url: null,
    print_header: '',
    print_footer: ''
  });
  
  const [idSeriesSettings, setIdSeriesSettings] = useState([]);
  const [whatsappTemplates, setWhatsappTemplates] = useState([]);
  const [pdfSettings, setPdfSettings] = useState([]);
  const [dashboardSettings, setDashboardSettings] = useState({});
  const [systemControls, setSystemControls] = useState({});

  useEffect(() => {
    fetchAllSettings();
  }, []);

  const fetchAllSettings = async () => {
    try {
      const [company, idSeries, whatsapp, pdf, dashboard, controls] = await Promise.all([
        api.get('/admin/settings/company'),
        api.get('/admin/settings/id-series'),
        api.get('/admin/settings/whatsapp-templates'),
        api.get('/admin/settings/pdf'),
        api.get('/admin/settings/dashboard'),
        api.get('/admin/settings/system-controls')
      ]);
      
      setCompanySettings(company.data);
      setIdSeriesSettings(idSeries.data);
      setWhatsappTemplates(whatsapp.data);
      setPdfSettings(pdf.data);
      setDashboardSettings(dashboard.data);
      setSystemControls(controls.data);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load settings');
      setLoading(false);
    }
  };

  const handleSaveCompany = async () => {
    try {
      setSaving(true);
      await api.put('/admin/settings/company', companySettings);
      clearSettingsCache();
      toast.success('Company settings saved');
    } catch (error) {
      toast.error('Failed to save company settings');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await api.post('/admin/settings/company/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setCompanySettings({ ...companySettings, logo_url: response.data.logo_url });
      toast.success('Logo uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload logo');
    }
  };

  const handleSaveIdSeries = async (module, config) => {
    try {
      await api.put(`/admin/settings/id-series/${module}`, config);
      toast.success('ID series updated');
      fetchAllSettings();
    } catch (error) {
      toast.error('Failed to save ID series');
    }
  };

  const handleSaveWhatsappTemplate = async (templateId, template) => {
    try {
      await api.put(`/admin/settings/whatsapp-templates/${templateId}`, template);
      toast.success('Template updated');
    } catch (error) {
      toast.error('Failed to save template');
    }
  };

  const handleSaveDashboard = async () => {
    try {
      setSaving(true);
      await api.put('/admin/settings/dashboard', dashboardSettings);
      toast.success('Dashboard settings saved');
    } catch (error) {
      toast.error('Failed to save dashboard settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSystemControls = async () => {
    try {
      setSaving(true);
      await api.put('/admin/settings/system-controls', systemControls);
      clearSettingsCache();
      toast.success('System controls saved');
    } catch (error) {
      toast.error('Failed to save system controls');
    } finally {
      setSaving(false);
    }
  };

  const getIdSeriesPreview = (config) => {
    const padding = config.padding_length || 4;
    const number = String(config.current_number || 1).padStart(padding, '0');
    return `${config.prefix}${config.separator}${number}`;
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 rounded w-1/4"></div>
          <div className="h-64 bg-slate-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="settings-page" className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
          System Settings
        </h1>
        <p className="text-slate-600 mt-1">Configure application settings and preferences</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3 lg:grid-cols-6 mb-6">
          <TabsTrigger value="company" className="text-xs md:text-sm">
            <Building2 className="h-4 w-4 mr-1 hidden md:inline" />
            Company
          </TabsTrigger>
          <TabsTrigger value="id-series" className="text-xs md:text-sm">
            <Hash className="h-4 w-4 mr-1 hidden md:inline" />
            ID Series
          </TabsTrigger>
          <TabsTrigger value="whatsapp" className="text-xs md:text-sm">
            <MessageCircle className="h-4 w-4 mr-1 hidden md:inline" />
            WhatsApp
          </TabsTrigger>
          <TabsTrigger value="pdf" className="text-xs md:text-sm">
            <FileText className="h-4 w-4 mr-1 hidden md:inline" />
            PDF
          </TabsTrigger>
          <TabsTrigger value="dashboard" className="text-xs md:text-sm">
            <LayoutDashboard className="h-4 w-4 mr-1 hidden md:inline" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="controls" className="text-xs md:text-sm">
            <SettingsIcon className="h-4 w-4 mr-1 hidden md:inline" />
            Controls
          </TabsTrigger>
        </TabsList>

        {/* Company Settings */}
        <TabsContent value="company">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Company Settings
              </CardTitle>
              <CardDescription>Configure your company information for documents and prints</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Company Name</Label>
                  <Input
                    value={companySettings.company_name}
                    onChange={(e) => setCompanySettings({...companySettings, company_name: e.target.value})}
                    placeholder="Your Company Name"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={companySettings.email || ''}
                    onChange={(e) => setCompanySettings({...companySettings, email: e.target.value})}
                    placeholder="company@example.com"
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={companySettings.phone || ''}
                    onChange={(e) => setCompanySettings({...companySettings, phone: e.target.value})}
                    placeholder="+91 98765 43210"
                  />
                </div>
                <div>
                  <Label>Logo</Label>
                  <div className="flex items-center gap-3">
                    {companySettings.logo_url && (
                      <img 
                        src={`${process.env.REACT_APP_BACKEND_URL}${companySettings.logo_url}`} 
                        alt="Logo" 
                        className="h-10 w-10 object-contain border rounded"
                      />
                    )}
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>
              <div>
                <Label>Address</Label>
                <Textarea
                  value={companySettings.address || ''}
                  onChange={(e) => setCompanySettings({...companySettings, address: e.target.value})}
                  placeholder="Full company address"
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Print Header</Label>
                  <Input
                    value={companySettings.print_header || ''}
                    onChange={(e) => setCompanySettings({...companySettings, print_header: e.target.value})}
                    placeholder="Header text for printed documents"
                  />
                </div>
                <div>
                  <Label>Print Footer</Label>
                  <Input
                    value={companySettings.print_footer || ''}
                    onChange={(e) => setCompanySettings({...companySettings, print_footer: e.target.value})}
                    placeholder="Footer text for printed documents"
                  />
                </div>
              </div>
              <Button onClick={handleSaveCompany} disabled={saving} className="bg-slate-900 hover:bg-slate-800">
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Saving...' : 'Save Company Settings'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ID Series Settings */}
        <TabsContent value="id-series">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Hash className="h-5 w-5" />
                ID Series Configuration
              </CardTitle>
              <CardDescription>Configure auto-generated ID formats for each module</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {idSeriesSettings.map((series, index) => (
                <div key={series.module} className="p-4 border rounded-lg space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold capitalize">{series.module.replace('_', ' ')}</h3>
                    <Badge variant="outline" className="font-mono">
                      Preview: {getIdSeriesPreview(series)}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <div>
                      <Label className="text-xs">Prefix</Label>
                      <Input
                        value={series.prefix}
                        onChange={(e) => {
                          const updated = [...idSeriesSettings];
                          updated[index].prefix = e.target.value;
                          setIdSeriesSettings(updated);
                        }}
                        className="h-9"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Separator</Label>
                      <Input
                        value={series.separator}
                        onChange={(e) => {
                          const updated = [...idSeriesSettings];
                          updated[index].separator = e.target.value;
                          setIdSeriesSettings(updated);
                        }}
                        className="h-9"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Padding</Label>
                      <Input
                        type="number"
                        min="1"
                        max="10"
                        value={series.padding_length}
                        onChange={(e) => {
                          const updated = [...idSeriesSettings];
                          updated[index].padding_length = parseInt(e.target.value);
                          setIdSeriesSettings(updated);
                        }}
                        className="h-9"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Reset</Label>
                      <Select
                        value={series.reset_option}
                        onValueChange={(v) => {
                          const updated = [...idSeriesSettings];
                          updated[index].reset_option = v;
                          setIdSeriesSettings(updated);
                        }}
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="never">Never</SelectItem>
                          <SelectItem value="yearly">Yearly</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-end">
                      <Button 
                        size="sm" 
                        onClick={() => handleSaveIdSeries(series.module, series)}
                        className="w-full bg-slate-900 hover:bg-slate-800"
                      >
                        <Save className="h-3.5 w-3.5 mr-1" />
                        Save
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* WhatsApp Templates */}
        <TabsContent value="whatsapp">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageCircle className="h-5 w-5" />
                WhatsApp Templates
              </CardTitle>
              <CardDescription>Configure message templates for WhatsApp notifications</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {whatsappTemplates.map((template, index) => (
                <div key={template.template_id} className="p-4 border rounded-lg space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold">{template.template_name}</h3>
                    <Badge variant="outline">{template.template_type}</Badge>
                  </div>
                  <div>
                    <Label className="text-xs">Message Template</Label>
                    <Textarea
                      value={template.message_template}
                      onChange={(e) => {
                        const updated = [...whatsappTemplates];
                        updated[index].message_template = e.target.value;
                        setWhatsappTemplates(updated);
                      }}
                      rows={4}
                      className="font-mono text-sm"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Available variables: {template.variables.map(v => `{${v}}`).join(', ')}
                    </p>
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={template.is_active}
                        onCheckedChange={(checked) => {
                          const updated = [...whatsappTemplates];
                          updated[index].is_active = checked;
                          setWhatsappTemplates(updated);
                        }}
                      />
                      <Label className="text-sm">Active</Label>
                    </div>
                    <Button 
                      size="sm" 
                      onClick={() => handleSaveWhatsappTemplate(template.template_id, template)}
                      className="bg-slate-900 hover:bg-slate-800"
                    >
                      <Save className="h-3.5 w-3.5 mr-1" />
                      Save Template
                    </Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* PDF Settings */}
        <TabsContent value="pdf">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                PDF Print Settings
              </CardTitle>
              <CardDescription>Configure PDF layout and appearance for each module</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {pdfSettings.map((settings, index) => (
                <div key={settings.module} className="p-4 border rounded-lg space-y-4">
                  <h3 className="font-semibold capitalize">{settings.module.replace('_', ' ')}</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={settings.show_header}
                        onCheckedChange={(checked) => {
                          const updated = [...pdfSettings];
                          updated[index].show_header = checked;
                          setPdfSettings(updated);
                        }}
                      />
                      <Label className="text-sm">Show Header</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={settings.show_footer}
                        onCheckedChange={(checked) => {
                          const updated = [...pdfSettings];
                          updated[index].show_footer = checked;
                          setPdfSettings(updated);
                        }}
                      />
                      <Label className="text-sm">Show Footer</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={settings.show_logo}
                        onCheckedChange={(checked) => {
                          const updated = [...pdfSettings];
                          updated[index].show_logo = checked;
                          setPdfSettings(updated);
                        }}
                      />
                      <Label className="text-sm">Show Logo</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={settings.show_signature}
                        onCheckedChange={(checked) => {
                          const updated = [...pdfSettings];
                          updated[index].show_signature = checked;
                          setPdfSettings(updated);
                        }}
                      />
                      <Label className="text-sm">Signature Area</Label>
                    </div>
                  </div>
                </div>
              ))}
              <p className="text-sm text-slate-500">Note: PDF settings will be applied when generating documents.</p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Dashboard Settings */}
        <TabsContent value="dashboard">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <LayoutDashboard className="h-5 w-5" />
                Dashboard Settings
              </CardTitle>
              <CardDescription>Configure default dashboard visibility and behavior</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { key: 'show_order_overview', label: 'Order Overview Section' },
                  { key: 'show_lr_status', label: 'LR Status Section' },
                  { key: 'show_quantity_summary', label: 'Quantity Summary Section' },
                  { key: 'show_item_analytics', label: 'Item Analytics Section' },
                  { key: 'show_charts', label: 'Analytics Charts' },
                  { key: 'show_alerts', label: 'Operational Alerts' },
                  { key: 'show_recent_orders', label: 'Recent Orders Section' }
                ].map(item => (
                  <div key={item.key} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <Label className="text-sm">{item.label}</Label>
                    <Switch
                      checked={dashboardSettings[item.key] !== false}
                      onCheckedChange={(checked) => setDashboardSettings({...dashboardSettings, [item.key]: checked})}
                    />
                  </div>
                ))}
              </div>
              <Button onClick={handleSaveDashboard} disabled={saving} className="bg-slate-900 hover:bg-slate-800">
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Saving...' : 'Save Dashboard Settings'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* System Controls */}
        <TabsContent value="controls">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <SettingsIcon className="h-5 w-5" />
                System Controls
              </CardTitle>
              <CardDescription>Enable or disable system-wide features</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                {[
                  { key: 'enable_whatsapp', label: 'WhatsApp Integration', description: 'Enable WhatsApp message sending' },
                  { key: 'enable_pdf_export', label: 'PDF Export', description: 'Allow PDF document generation' },
                  { key: 'enable_audit_logs', label: 'Audit Logs', description: 'Track system activity and changes' },
                  { key: 'enable_validation_alerts', label: 'Validation Alerts', description: 'Show validation warnings and alerts' },
                  { key: 'enable_notifications', label: 'Notifications', description: 'Enable in-app notifications' }
                ].map(item => (
                  <div key={item.key} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div>
                      <Label className="text-sm font-medium">{item.label}</Label>
                      <p className="text-xs text-slate-500">{item.description}</p>
                    </div>
                    <Switch
                      checked={systemControls[item.key] !== false}
                      onCheckedChange={(checked) => setSystemControls({...systemControls, [item.key]: checked})}
                    />
                  </div>
                ))}
              </div>
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">Important</p>
                  <p className="text-xs text-amber-700">Disabling features will affect all users. Changes take effect immediately.</p>
                </div>
              </div>
              <Button onClick={handleSaveSystemControls} disabled={saving} className="bg-slate-900 hover:bg-slate-800">
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Saving...' : 'Save System Controls'}
              </Button>
            </CardContent>
          </Card>

          {/* TEMPORARY: Database Reset — Danger Zone */}
          <Card data-testid="danger-zone-card" className="border-red-300 mt-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-700">
                <Trash2 className="h-5 w-5" />
                Danger Zone
              </CardTitle>
              <CardDescription className="text-red-600">
                Irreversible actions. Proceed with extreme caution.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg space-y-3">
                <div>
                  <p className="text-sm font-medium text-red-800">Reset Database</p>
                  <p className="text-xs text-red-600 mt-1">
                    Deletes ALL operational data (orders, bills, receipts, LRs, masters, users). System configuration (roles, permissions, settings) is preserved. A new admin account (admin@sahil.com) will be created.
                  </p>
                </div>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button data-testid="reset-database-btn" variant="destructive" disabled={resetting} className="bg-red-600 hover:bg-red-700">
                      <Trash2 className="h-4 w-4 mr-2" />
                      {resetting ? 'Resetting...' : 'Reset Database'}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle className="text-red-700">Are you sure you want to reset the database?</AlertDialogTitle>
                      <AlertDialogDescription className="space-y-2">
                        <span className="block font-semibold text-red-600">This action cannot be undone.</span>
                        <span className="block">All orders, bills, receipts, LR reports, masters, notifications, audit logs, and users will be permanently deleted.</span>
                        <span className="block">Only system configuration will be preserved.</span>
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        data-testid="confirm-reset-btn"
                        className="bg-red-600 hover:bg-red-700"
                        onClick={async () => {
                          setResetting(true);
                          try {
                            const res = await api.post('/admin/reset-database');
                            toast.success(`Database reset complete. New admin: ${res.data.new_admin}`);
                            setTimeout(() => { logout(); window.location.href = '/login'; }, 1500);
                          } catch (err) {
                            toast.error(err.response?.data?.detail || 'Failed to reset database');
                            setResetting(false);
                          }
                        }}
                      >
                        Yes, Reset Everything
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Settings;
